import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Pencil, Check, Eye, Trash } from 'lucide-react';

import {
  Card,
  CardContent,
  Heading,
  Button,
  type ButtonProps,
  FieldWithLabel,
  Input,
  Textarea,
} from '@app/shared/ui/atoms/Common';
import { HtmlContent } from '@app/shared/ui/atoms/Admin/MatrixChannelForm';
import { useAdminMatrixChannelFormTranslations } from '@app/shared/hooks/admin/useAdminMatrixChannelFormTranslations';
import { capitalize, cn } from '@app/shared/utils/common';
import { Story } from '@app/shared/types/admin/pages/matrixChannelForm';

enum Mode {
  DEFAULT = 'default',
  EDITING = 'editing',
  PREVIEW = 'preview',
}

interface StoryRowProps {
  story: Story;
  onUpdate: (story: Story) => void;
  onRemove: () => void;
}

function StoryRowButton(props: Omit<ButtonProps, 'variant' | 'size' | 'className'>) {
  return <Button variant="tertiary" size="sm" className="w-fit px-2" {...props} />;
}

export function StoryRow({ story, onUpdate, onRemove }: StoryRowProps) {
  const {
    handleSubmit,
    register,
    reset,
    getValues,
    formState: { errors },
  } = useForm<Story>({ defaultValues: story });
  const [mode, setMode] = useState(Mode.DEFAULT);
  const { getTranslation, requiredError } = useAdminMatrixChannelFormTranslations();

  useEffect(() => {
    reset(story);
  }, [story]);

  const handleUpdate = (values: Story) => {
    onUpdate(values);
    setMode(Mode.DEFAULT);
  };

  const handleEditingBtnClick = () => {
    setMode(Mode.EDITING);
  };

  const handlePreviewBtnClick = () => {
    if (mode === Mode.PREVIEW) {
      setMode(Mode.DEFAULT);
    } else {
      onUpdate(getValues());
      setMode(Mode.PREVIEW);
    }
  };

  const { title, description } = getValues();
  const noTitleLabel = getTranslation('no_title');
  const titleLabel = getTranslation('title_optional');
  const titlePlaceholder = getTranslation('title');
  const descriptionLabel = capitalize(getTranslation('description'));
  const descriptionRequiredErrorMessage = requiredError('description');

  return (
    <Card className="bg-blue-light shadow-md">
      <CardContent className="p-2">
        <form onSubmit={handleSubmit(handleUpdate)} className="flex flex-col gap-4">
          <div className="flex items-center">
            <Heading level={5} className="mr-2 flex-1 truncate">
              {story.title || <span className="text-secondary-foreground">{noTitleLabel}</span>}
            </Heading>

            {mode === Mode.EDITING && (
              <StoryRowButton type="submit">
                <Check size={16} className="text-accent" />
              </StoryRowButton>
            )}
            {mode !== Mode.EDITING && (
              <StoryRowButton type="button" onClick={handleEditingBtnClick}>
                <Pencil size={16} />
              </StoryRowButton>
            )}
            <StoryRowButton type="button" onClick={handlePreviewBtnClick}>
              <Eye size={16} className={cn(mode === Mode.PREVIEW && 'text-accent')} />
            </StoryRowButton>
            <StoryRowButton type="button" onClick={onRemove}>
              <Trash size={16} className="text-red-500" />
            </StoryRowButton>
          </div>

          {[Mode.EDITING, Mode.PREVIEW].includes(mode) && (
            <Card className="shadow-none">
              <CardContent className="p-2">
                <FieldWithLabel label={titleLabel}>
                  {mode === Mode.EDITING ? (
                    <Input {...register('title')} placeholder={titlePlaceholder} />
                  ) : (
                    <HtmlContent html={title} />
                  )}
                </FieldWithLabel>
                <FieldWithLabel
                  label={descriptionLabel}
                  hasErrorField
                  error={errors.description?.message}
                >
                  {mode === Mode.EDITING ? (
                    <Textarea
                      {...register('description', {
                        required: { value: true, message: descriptionRequiredErrorMessage },
                      })}
                      placeholder={descriptionLabel}
                    />
                  ) : (
                    <HtmlContent html={description} />
                  )}
                </FieldWithLabel>
              </CardContent>
            </Card>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
